<template>
	<section class="pc-jiaodian" :class="{ 'pc-jiaodian--index': isIndexType }">
		<div class="pc-card-title-container">
			<h2 class="pc-card-title">{{ t("square.article.title") }}</h2>

			<button v-if="isIndexType" class="index-more-btn" type="button" @click="toJiaodian">
				{{ t("square.article.more") }}
			</button>
			<MoreButton v-else :title="t('square.article.viewMore')" :onClick="toJiaodian" />
		</div>
		<div class="tab" v-if="!isIndexType">
			<div
				class="tab-item"
				v-for="item in poolList"
				:class="{ active: item.value === active }"
				:key="item.value"
				@click="
					() => {
						if (loading) return;
						if (isIndexType) {
							logEvent(`home_Featured_click`);
						} else {
							logEvent(`Square_Focus_click`);
						}
						active = item.value;
					}
				"
			>
				<span>{{ item.value == 0 ? t("square.filter.all") : item.label }}</span>
			</div>
		</div>
		<n-spin :show="loading && page === 1">
			<div v-if="isIndexType && indexFocusList.length > 0" class="index-grid">
				<article
					v-for="(item, index) in indexFocusList"
					:key="item.id"
					class="focus-card"
					:ref="el => bindArticleCardRef(el, item, index)"
					@click="handleToSquareDetail(item, index)"
				>
					<div class="focus-card__cover-wrap">
						<img class="focus-card__cover" :src="item.cover" :alt="item.title" />
						<span
							v-if="item.type == 1"
							class="focus-card__play"
							aria-hidden="true"
							@click.stop="handleVideoPlayClick(item, index)"
						></span>
					</div>
					<div class="focus-card__body">
						<p class="focus-card__title">{{ item.title }}</p>
						<div class="focus-card__meta">
							<div class="focus-card__author">
								<img
									class="focus-card__avatar"
									:src="getAuthorAvatar(item)"
									:alt="getAuthorName(item)"
								/>
								<span>{{ getAuthorName(item) }}</span>
							</div>
							<div class="focus-card__views">
								<img :src="viewIcon" alt="" aria-hidden="true" />
								<span>{{ item.view_count ?? 0 }}</span>
							</div>
						</div>
					</div>
				</article>
			</div>
			<ul v-else-if="data.length > 0" class="hot-list">
				<li v-for="(item, index) in data" :key="item.id" @click="handleToSquareDetail(item, index)">
					<div class="content">
						<div class="left">
							<img style="object-fit: cover" :src="item.cover" alt="" class="content-pic" />
							<img src="../../../images/vedio2.png" alt="" class="vedio" v-if="item.type == 1" />
							<div class="mask" v-if="item.type == 1"></div>
							<div class="time" v-if="item.type == 1">
								{{ formatTime(item.media_second) }}
							</div>
						</div>
						<div class="right">
							<div class="line-one">
								<p class="one-title">
									<span class="one-tab">{{
										item.type == 1 ? t("square.contentType.video") : t("square.contentType.article")
									}}</span>
									{{ item.title }}
								</p>
							</div>
							<p class="line-two">
								{{ item.desc }}
							</p>
							<div class="line-three">
								<span>{{ formatSimpleViewCount(item.view_count) }}</span>
								<span>{{ formatDate(item.updated_at) }}</span>
							</div>
							<div class="line-four">
								<span
									v-for="(i, index) in getTag(item.category)"
									:key="index"
									class="four-tag"
									:style="{
										background: `linear-gradient(to bottom, ${i.color1}, ${i.color})`,
									}"
								>
									{{ i.label }}
								</span>
							</div>
						</div>
					</div>
				</li>
			</ul>
			<div class="load-text" v-if="!isIndexType">
				<div class="load-btn" v-if="!isFinish && !loading" @click="page += 1">
					<span>{{ t("square.load.more") }}</span>
					<img src="../../../images/more.png" alt="" />
				</div>
				<div class="load-btn loading" v-else-if="!isFinish && loading" @click="page += 1">
					<span>{{ t("square.load.loading") }}</span>
				</div>
				<div class="no-more" v-else>
					<span>{{ t("square.load.done") }}</span>
				</div>
			</div>
		</n-spin>
	</section>
</template>

<script setup>
import { computed, inject, onMounted, ref, watch } from "vue";
import { getJiaodianList, getSquareContentImageText } from "@/api/api";
import { useSquarePcPageData } from "@variants/seer/square/composables/useSquarePageData";
import MoreButton from "~/components/pc/more-button/index.vue";
import { useI18n } from "~/composables/useI18n";
import {
	buildContentParams,
	MEDIA_TYPES,
	useMediaCardImpression,
} from "../../../composables/useMediaSquareTracking";
import viewIcon from "../../../images/eye.png";
import channelAvatar from "../../../images/video_avatar.png";

const { t, locale } = useI18n();

const formatSimpleViewCount = count => {
	return t("square.views.simple", { count: Number(count) || 0 });
};

const props = defineProps({
	isIndexType: {
		type: Boolean,
		default: false,
	},
});

const pageData = useSquarePcPageData();
const indexFocusList = computed(() =>
	props.isIndexType ? (pageData.value?.focusList || []).slice(0, 8) : []
);

const page = ref(1);
const size = ref(props.isIndexType ? 5 : 6);
const total = ref(0);
const data = ref([]);
const filteredData = ref([]); // 存储筛选后的数据
const loading = ref(false);

const isFinish = computed(() => data.value.length >= total.value);

// 注入父组件提供的方法
const formatTime = inject("formatTime");
const formatDate = inject("formatDate");
const toSquareDetail = inject("toSquareDetail");
const mediaTracking = inject("mediaSquareTracking", null);

const { bindCardRef: bindArticleCardRef } = useMediaCardImpression({
	track: (eventName, params) => mediaTracking?.track(eventName, params),
	impressionEvent: "article_card_impression",
	getItemParams: (item, index) =>
		buildContentParams(item, {
			position: index,
			formatTime,
			authorFallback: t("square.article.defaultAuthor"),
		}),
});

const handleToSquareDetail = (item, index = 0) => {
	mediaTracking?.trackArticleCardClick(item, index, formatTime, t("square.article.defaultAuthor"));
	toSquareDetail(item);
};

const handleVideoPlayClick = (item, index) => {
	mediaTracking?.trackVideoPlayClick(item, {
		mediaType: item?.type == 1 ? MEDIA_TYPES.ARTICLE : MEDIA_TYPES.ARTICLE,
		position: index,
		formatTime,
		authorFallback: t("square.article.defaultAuthor"),
	});
	toSquareDetail(item);
};

const poolList = ref([]);
const active = ref(0);

onMounted(async () => {
	if (props.isIndexType) return;

	const res = await getJiaodianList({
		enum: "SquareCategory",
	});
	poolList.value = res.data || [];
	active.value = poolList.value[0]?.value ?? 0;
});

const toJiaodian = () => {
	mediaTracking?.trackMediaMoreClick({
		sectionId: "article",
		mediaType: MEDIA_TYPES.ARTICLE,
		currentCount: props.isIndexType ? indexFocusList.value.length : data.value.length,
	});
	navigateTo("/seer/square/article");
};

const getAuthorName = item =>
	item?.nickname || item?.author_name || t("square.article.defaultAuthor");

const getAuthorAvatar = item => item?.avatar || channelAvatar;

const tagStyles = [
	{ id: 1, color1: "#DE5BCB", color: "#D68FE2" },
	{ id: 2, color1: "#DEB05B", color: "#E2C58F " },
	{ id: 3, color1: "#C98360", color: "#E3B495" },
	{ id: 4, color1: "#7C5BDE", color: "#A48FE2" },
	{ id: 5, color1: "#5BB0DE", color: "#8FC5E2" },
	{ id: 6, color1: "#DE615B", color: "#E2938F" },
	{ id: 7, color1: "#5B89DE", color: "#8FACE2" },
	{ id: 8, color1: "#5BB0DE", color: "#8FC5E2" },
];
const getTag = categoryStr => {
	if (!categoryStr || categoryStr == "0," || categoryStr == "0") return [];

	const categoryIds = categoryStr
		.split(",")
		.filter(id => id !== "")
		.map(Number);

	return tagStyles
		.filter(tag => categoryIds.includes(tag.id))
		.map(tag => ({
			...tag,
			label: t(`square.categories.${tag.id}`),
		}));
};

const getList = () => {
	if (loading.value) return;
	loading.value = true;
	getSquareContentImageText({
		page: page.value,
		size: size.value,
	}).then(res => {
		const list = res?.data?.list || [];
		const pager = res?.data?.pager || {};
		if (page.value === 1) {
			data.value = list;
		} else {
			data.value.push(...list);
		}
		total.value = pager.total_count || 0;
		loading.value = false;
	});
};

watch(
	() => active.value,
	() => {
		if (props.isIndexType) return;
		page.value = 1;
		total.value = 0;
		getList();
	}
);

watch(
	() => page.value,
	() => {
		if (props.isIndexType) return;
		getList();
	}
);

watch(
	() => poolList.value,
	newVal => {
		if (props.isIndexType) return;
		if (newVal.length) {
			active.value = newVal[0].value;
			getList();
		}
	},
	{ immediate: true }
);

watch(locale, () => {
	if (props.isIndexType) {
		refreshNuxtData("square-pc-index-data");
		return;
	}

	page.value = 1;
	total.value = 0;
	getJiaodianList({
		enum: "SquareCategory",
	}).then(res => {
		poolList.value = res.data || [];
	});
});
</script>

<style lang="scss" scoped>
@mixin shenlue {
	display: -webkit-box; /* 使用旧版 Flexbox 模型（关键） */
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.5;
}

.pc-jiaodian {
	width: 100%;
	margin-bottom: 30px;
	box-sizing: border-box;
	border-radius: 20px;
	box-shadow: 0 4px 24.6px 0 rgba(0, 0, 0, 0.1);
	padding: 30px;

	.pc-card-title-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		padding-right: 10px;

		.pc-card-title {
			font-weight: 600;
			font-size: 28px;
			color: #333;
			line-height: 48px;
		}
	}

	.index-more-btn {
		font-size: 18px;
		color: #999999;
		padding: 3px 30px;
		font-weight: normal;
		border-radius: 8px;
		border: 1px solid #dfdfdf;
		background-color: transparent;
	}

	.tab {
		margin: 20px 0 40px;
		display: flex;
		flex-wrap: wrap;
		gap: 24px;

		.tab-item {
			padding: 0 24px;
			height: 40px;
			font-weight: 400;
			font-size: 18px;
			color: #c67f34;
			line-height: 38px;
			border-radius: 10px;
			border: 1px solid #c67f34;
			cursor: pointer;
		}

		.active {
			padding: 0 26px;
			line-height: 40px;
			color: #ffffff;
			background: linear-gradient(90deg, #d99a5e, #bd7430);
			border: none;
		}
	}

	.index-grid {
		display: grid;
		grid-template-columns: repeat(4, minmax(0, 1fr));
		gap: 16px;
	}

	.focus-card {
		display: flex;
		flex-direction: column;
		height: 100%;
		background: #ffffff;
		border: 1px solid #efefef;
		border-radius: 10px;
		overflow: hidden;
		cursor: pointer;
		transition: box-shadow 0.2s ease;

		&:hover {
			box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
		}

		&__cover-wrap {
			position: relative;
			border-radius: 10px 10px 0 0;
			overflow: hidden;
		}

		&__cover {
			display: block;
			width: 100%;
			aspect-ratio: 47 / 65;
			object-fit: cover;
		}

		&__play {
			position: absolute;
			top: 10px;
			right: 10px;
			width: 28px;
			height: 28px;
			border-radius: 50%;
			background: rgba(74, 74, 74, 0.6);

			&::before {
				content: "";
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-35%, -50%);
				border-top: 6px solid transparent;
				border-bottom: 6px solid transparent;
				border-left: 9px solid #fff;
			}
		}

		&__body {
			display: flex;
			flex-direction: column;
			flex: 1;
			padding: 10px 10px 20px;
		}

		&__title {
			margin: 0 0 10px;
			min-height: calc(1.4em * 2);
			color: #333;
			font-size: 16px;
			line-height: 1.4;
			font-weight: 500;
			@include shenlue;
		}

		&__meta {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 6px;
			margin-top: auto;
			color: #a0a0a0;
			font-size: 14px;
		}

		&__author,
		&__views {
			display: inline-flex;
			align-items: center;
			gap: 6px;
			min-width: 0;
		}

		&__author span {
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		&__avatar {
			width: 20px;
			height: 20px;
			border-radius: 50%;
			flex-shrink: 0;
		}

		&__views {
			flex-shrink: 0;
		}

		&__views img {
			width: 16px;
			height: 16px;
		}
	}

	.hot-list {
		display: flex;
		flex-direction: column;
		border-radius: 10px;
		overflow: hidden;

		li {
			position: relative;
			width: 100%;
			height: 310px;
			height: 278px;
			background-color: #ffffff;
			cursor: pointer;
			padding: 0 25px;
			box-sizing: border-box;

			.content {
				width: 100%;
				height: 100%;
				display: flex;
				border-bottom: 1px dotted #b0b0b0;
				padding: 35px 0;

				.left {
					position: relative;
					flex-shrink: 0;
					width: 368px;
					height: 207px;
					margin-right: 20px;
					border-radius: 10px;
					overflow: hidden;
					&:hover {
						.mask,
						.time,
						.vedio {
							opacity: 0;
						}
					}

					img {
						width: 100%;
						height: 100%;
					}

					.vedio {
						position: absolute;
						top: 20px;
						right: 20px;
						width: 7%;
						height: auto;
						opacity: 1;
						transition: opacity 0.3s ease;
					}

					.mask {
						position: absolute;
						width: 100%;
						height: 100%;
						top: 0;
						background: linear-gradient(
							to bottom,
							rgba(0, 0, 0, 0.4) 0%,
							rgba(0, 0, 0, 0) 20%,
							rgba(0, 0, 0, 0) 65%,
							rgba(0, 0, 0, 0.4) 100%
						);
						pointer-events: none;
						opacity: 1;
						transition: opacity 0.3s ease;
					}

					.time {
						position: absolute;
						bottom: 8px;
						right: 10px;
						font-size: 14px;
						color: #ffffff;
						opacity: 1;
						transition: opacity 0.3s ease;
					}
				}

				.right {
					font-weight: 400;
					flex: 1;
					min-width: 0;
					height: 207px;

					.line-one {
						padding: 10px 10px 10px 0;

						.one-tab {
							font-size: 12px;
							color: #c67f34;
							border: 1px solid #c67f34;
							padding: 0 2px;
							border-radius: 5px;
							margin-right: 10px;
							position: relative;
							top: -2px;
						}

						.one-title {
							font-size: 20px;
							color: #222222;
							font-weight: 500;
							width: 100%;
							@include shenlue;
						}
					}

					.line-two {
						font-size: 16px;
						color: #585858;
						margin: 10px 0;
						width: 100%;
						@include shenlue;
					}

					.line-three {
						font-size: 14px;
						color: #666666;

						span {
							margin-right: 10px;
						}
					}

					.line-four {
						font-size: 14px;
						color: #ffffff;
						margin-top: 10px;

						.four-tag {
							background-color: pink;
							border-radius: 5px;
							margin-right: 8px;
							padding: 0 3px;
						}
					}
				}
			}

			&:last-child .content {
				border-bottom: none;
			}
		}
	}

	.load-text {
		margin: 32px auto 0;

		.no-more {
			height: 14px;
			font-weight: 400;
			font-size: 14px;
			color: #c67f34;
			line-height: 14px;
			text-align: center;
		}

		.load-btn {
			margin: 0 auto;
			padding: 0 9px 0 17px;
			display: flex;
			align-items: center;
			width: 120px;
			height: 30px;
			border-radius: 15px;
			border: 1px solid #c67f34;
			font-weight: 400;
			font-size: 14px;
			color: #c67f34;
			line-height: 30px;
			cursor: pointer;

			img {
				margin-left: auto;
				width: 14px;
				height: 14px;
			}
		}

		.loading {
			padding: 0;
			justify-content: center;
		}
	}

	.loading {
		text-align: center;
	}
}

.pc-jiaodian--index {
	.pc-card-title-container {
		padding-right: 0;
		margin-bottom: 30px;

		.pc-card-title {
			font-size: 32px;
			color: #333333;
			font-weight: bold;
		}
	}
}

:deep(.n-spin) {
	color: #b9703a;
}
</style>
